import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Stack } from '@mui/material';
import { KeyboardEvent, MouseEvent } from 'react';
import useWidth from '../../../../../components/Hooks/useWidth';
import ProductAvailabilityChip from '../../../../../components/Product/ProductAvailabilityChip';
import SelloutProductTag from '../../../../../components/Product/product-cells/SelloutProductTag';
import SuggestedProductTag from '../../../../../components/Product/product-cells/SuggestedProductTag';
import ColorInfo from '../ColorInfo';
import useProductDetailsCombinationColorAndTags from './hooks/useProductDetailsCombinationColorAndTags';
import { useProductDetailsCombinationColorAndTagsStyles } from './productDetailsCombinationColorAndTags.styles';
import { ProductDetailsCombinationColorAndTagsProps } from './types/productDetailsCombinationColorAndTags.types';

const ACTIVATION_KEYS = ['Enter', ' '];

const ProductDetailsCombinationColorAndTags = ({
  productId,
  productColorId,
  productName,
  availability,
  productColorName,
  productColorCode,
  showSuggestedGrid,
  availabilityLabel,
  productColorHexCode,
  selloutSummary,
  productColorImageUrl,
  deliveryWindowEndDate,
  suggestedGridQuantity,
  deliveryWindowStartDate,
  showColorIcon = true,
  onClickProductName,
}: ProductDetailsCombinationColorAndTagsProps) => {
  const { mobileWidth } = useWidth();
  const { classes } = useProductDetailsCombinationColorAndTagsStyles();
  const { selectMediaColor } = useProductDetailsCombinationColorAndTags({
    productId,
    productColorId,
  });

  const handleProductNameKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (!onClickProductName || !ACTIVATION_KEYS.includes(event.key)) {
      return;
    }

    event.preventDefault();
    onClickProductName(event as unknown as MouseEvent<HTMLDivElement>);
  };

  return (
    <Stack alignItems="flex-start" sx={{ gap: 0.5, maxWidth: mobileWidth ? '100%' : 'inherit' }}>
      {productName && (
        <Tooltip fullWidth tabIndex={-1} TooltipContent={productName}>
          <Typography
            noWrap
            variant="body2"
            color="textPrimary"
            role={onClickProductName ? 'button' : undefined}
            tabIndex={-1}
            onClick={onClickProductName}
            onKeyDown={handleProductNameKeyDown}
            sx={{
              maxWidth: '100%',
              fontWeight: 'medium',
              ...(onClickProductName && {
                '&:hover': { cursor: 'pointer', color: 'primary.main' },
              }),
            }}
          >
            {productName}
          </Typography>
        </Tooltip>
      )}

      <ColorInfo
        name={productColorName}
        code={productColorCode}
        showColorIcon={showColorIcon}
        hexCode={productColorHexCode}
        imageUrl={productColorImageUrl}
        onClick={selectMediaColor}
      />

      <Stack
        direction="row"
        alignItems="center"
        sx={{ gap: 0.5, width: '100%', maxWidth: 'inherit' }}
      >
        <Box sx={{ display: 'flex', flex: '0 1 auto', overflow: 'hidden' }}>
          <ProductAvailabilityChip
            maxWidth="100%"
            variant="default"
            className={classes.availabilityTag}
            availability={availability}
            label={availabilityLabel}
            deliveryWindowStartDate={deliveryWindowStartDate}
            deliveryWindowEndDate={deliveryWindowEndDate}
          />
        </Box>

        {showSuggestedGrid && (
          <SuggestedProductTag className={classes.iconTag} quantity={suggestedGridQuantity} />
        )}

        {!!selloutSummary?.hasSelloutValues && (
          <SelloutProductTag
            className={classes.iconTag}
            quantity={selloutSummary.totalQuantitySold}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default ProductDetailsCombinationColorAndTags;
